import React from 'react';
import PropTypes from 'prop-types';
import { Table, CellText, Badge, Button, Spinner, Alert, EmptyState, BarChart03 } from '@opendate-app/setlist';
import {
  getStageLabel,
  getStageBadgeColor,
  getTypeLabel,
  formatAmount,
  formatCloseDate,
  formatProbability,
} from './dealStages';
import { countLabel } from './DealsPipelineSummary';

const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-3xl)' };
const sectionTitle = { margin: '0 0 var(--setlist-spacing-md)' };

const money = (value) => formatAmount(Number(value || 0));

function amountColumns(labels, openOnly) {
  return [
    { key: 'count', header: openOnly ? `Open ${labels.deal.plural.toLowerCase()}` : labels.deal.plural, align: 'right', render: (row) => Number(row.count).toLocaleString() },
    { key: 'amount', header: 'Amount', align: 'right', render: (row) => money(row.amount) },
    { key: 'weighted_amount', header: 'Weighted', align: 'right', render: (row) => money(row.weighted_amount) },
    { key: 'without_probability_count', header: 'No probability', align: 'right', render: (row) => Number(row.without_probability_count || 0).toLocaleString() },
  ];
}

function ReportSection({ title, children }) {
  return (
    <section>
      <h3 className="figma-text md semibold" style={sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

ReportSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function emptyRow(text) {
  return <div className="figma-text tertiary sm" style={{ padding: 'var(--setlist-spacing-xl)' }}>{text}</div>;
}

function DealsReportView({ team, report, loading, error, labels, types = [] }) {
  const pluralLower = labels.deal.plural.toLowerCase();

  if (error) {
    return <Alert color="error" title="Failed to load the report" description={error} />;
  }

  if (!report || !report.by_stage) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner label={loading ? 'Loading report…' : undefined} />
      </div>
    );
  }

  const openOnly = (report.breakdown_statuses || ['open']).join() === 'open';
  const hasAny = report.by_stage.length > 0
    || report.due_soon.length > 0
    || Number(report.totals.open_count) > 0
    || Number(report.totals.deferred_count) > 0;
  const breakdownNoun = `${openOnly ? 'open ' : ''}${pluralLower}`;

  if (!hasAny) {
    return (
      <EmptyState
        size="sm"
        icon={BarChart03}
        title={`No ${breakdownNoun} to report on`}
        description={`Open ${pluralLower} that match your filters show up here by stage, solicitor and campaign.`}
      />
    );
  }

  const stageColumns = [
    {
      key: 'stage',
      header: 'Stage',
      primary: true,
      render: (row) => <Badge color={getStageBadgeColor(row.stage)} dot>{getStageLabel(row.stage)}</Badge>,
    },
    { key: 'category', header: 'Category', render: (row) => getTypeLabel(row.category, types) },
    ...amountColumns(labels, openOnly),
  ];

  const labelColumn = (header) => ({ key: 'label', header, primary: true, render: (row) => row.label });

  const dueColumns = [
    {
      key: 'name',
      header: labels.deal.singular,
      primary: true,
      render: (row) => (
        <CellText
          primary={<Button as="a" href={`/teams/${team.id}/crm/deals/${row.id}`} variant="link-color">{row.name}</Button>}
          supporting={row.donor_name || undefined}
        />
      ),
    },
    { key: 'stage', header: 'Stage', render: (row) => <Badge color={getStageBadgeColor(row.stage)} dot>{getStageLabel(row.stage)}</Badge> },
    { key: 'close_date', header: 'Close date', render: (row) => formatCloseDate(row.close_date) },
    { key: 'amount', header: 'Amount', align: 'right', render: (row) => formatAmount(row.amount) },
    { key: 'probability', header: 'Probability', align: 'right', render: (row) => formatProbability(row.probability) },
    { key: 'weighted_amount', header: 'Weighted', align: 'right', render: (row) => formatAmount(row.weighted_amount) },
    { key: 'owner_name', header: 'Solicitor', render: (row) => row.owner_name || '—' },
  ];

  const deferred = Number(report.totals.deferred_count || 0);
  const dueCount = Number(report.totals.due_soon_count || 0);

  return (
    <div style={{ ...stack, opacity: loading ? 0.5 : 1 }} aria-busy={loading} data-testid="deals-report-view">
      {deferred > 0 && (
        <div className="figma-text tertiary sm">
          {`${countLabel(deferred, labels)} deferred (${money(report.totals.deferred_amount)}) ${deferred === 1 ? 'is' : 'are'} not counted as open.`}
        </div>
      )}

      <ReportSection title="Stage capacity">
        <Table columns={stageColumns} data={report.by_stage} getRowKey={(row) => `${row.category}-${row.stage}`} stackBelow={640} empty={emptyRow(`No ${breakdownNoun}.`)} />
      </ReportSection>

      <ReportSection title="By solicitor">
        <Table columns={[labelColumn('Solicitor'), ...amountColumns(labels, openOnly)]} data={report.by_solicitor} getRowKey={(row) => `solicitor-${row.id}`} stackBelow={640} empty={emptyRow(`No ${breakdownNoun}.`)} />
      </ReportSection>

      <ReportSection title="By campaign">
        <Table columns={[labelColumn('Campaign'), ...amountColumns(labels, openOnly)]} data={report.by_campaign} getRowKey={(row) => `campaign-${row.id}`} stackBelow={640} empty={emptyRow(`No ${breakdownNoun}.`)} />
      </ReportSection>

      <ReportSection title={`Due in ${report.totals.due_soon_days || 30} days`}>
        {dueCount > report.due_soon.length && (
          <div className="figma-text tertiary sm" style={{ marginBottom: 'var(--setlist-spacing-md)' }}>
            {`Showing the ${report.due_soon.length} soonest of ${countLabel(dueCount, labels)}.`}
          </div>
        )}
        <Table columns={dueColumns} data={report.due_soon} getRowKey={(row) => row.id} stackBelow={640} empty={emptyRow(`No open ${pluralLower} close in the next ${report.totals.due_soon_days || 30} days.`)} />
      </ReportSection>
    </div>
  );
}

DealsReportView.propTypes = {
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }).isRequired,
  report: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  labels: PropTypes.object.isRequired,
  types: PropTypes.arrayOf(PropTypes.object),
};

export default DealsReportView;
